import * as i0 from '@angular/core';
import { Injectable, ɵmakeDecorator } from '@angular/core';
import * as i1 from '@helix/platform/shared/api';
import { RX_BUNDLE, DataPage, RX_APPLICATION, RX_DESIGNER, RX_DESIGNER_ELEMENT_SHAPE } from '@helix/platform/shared/api';
import * as i2 from '@ngx-translate/core';
import { reject, chain, map as map$1, cloneDeep, forEach, find, filter, transform, get, isEmpty, includes, isPlainObject, isArray, compact, remove, last, set } from 'lodash';
import { BehaviorSubject, combineLatest, of, forkJoin } from 'rxjs';
import { map, catchError, shareReplay, switchMap, tap } from 'rxjs/operators';
import * as i1$1 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import * as i2$1 from '@helix/platform/utils';

class RxProcessDataDictionaryService {
    constructor(rxDataDictionaryUtils, rxGlobalCacheService, translateService) {
        this.rxDataDictionaryUtils = rxDataDictionaryUtils;
        this.rxGlobalCacheService = rxGlobalCacheService;
        this.translateService = translateService;
        this.activitiesDataDictionarySubject = new BehaviorSubject(null);
        this.errorsDataDictionarySubject = new BehaviorSubject(null);
        this.activitiesDataDictionaryBranch$ = this.activitiesDataDictionarySubject.pipe(map((activitiesDataDictionary) => ({
            label: this.translateService.instant('com.bmc.arsys.rx.client.process-designer.activities.label'),
            expanded: true,
            children: activitiesDataDictionary
        })));
        this.errorsDataDictionaryBranch$ = this.errorsDataDictionarySubject.pipe(map((errorsDataDictionary) => ({
            label: this.translateService.instant('com.bmc.arsys.rx.client.common.errors.label'),
            expanded: true,
            children: errorsDataDictionary
        })));
        this.functionsDataDictionaryBranch$ = this.rxGlobalCacheService
            .getFunctionDescriptors()
            .pipe(map((functionDescriptors) => reject(this.rxDataDictionaryUtils.getFunctionsDataDictionaryBranch(this.getFunctionDataDictionaryDescriptors(functionDescriptors)), { label: 'Rule qualification' })), map((functionsDataDictionaryBranch) => ({
            label: this.translateService.instant('com.bmc.arsys.rx.client.common.functions.label'),
            expanded: true,
            children: functionsDataDictionaryBranch
        })));
        this.variablesDataDictionaryBranchSubject = new BehaviorSubject(null);
        this.commonDataDictionary$ = combineLatest([
            this.activitiesDataDictionaryBranch$,
            this.errorsDataDictionaryBranch$,
            this.functionsDataDictionaryBranch$,
            this.variablesDataDictionaryBranchSubject
        ]).pipe(map(([activitiesDataDictionaryBranch, errorsDataDictionaryBranch, functionsDataDictionaryBranch, variablesDataDictionaryBranch]) => {
            return [
                variablesDataDictionaryBranch,
                {
                    label: this.translateService.instant('com.bmc.arsys.rx.client.common.general-items.label'),
                    children: [
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-date.label'),
                            icon: 'd-icon-dollar',
                            expression: '$DATE$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-date-time.label'),
                            icon: 'd-icon-dollar',
                            expression: '$TIMESTAMP$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-group-ids.label'),
                            icon: 'd-icon-dollar',
                            expression: '$GROUPIDS$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-groups.label'),
                            icon: 'd-icon-dollar',
                            expression: '$GROUPS$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-roles.label'),
                            icon: 'd-icon-dollar',
                            expression: '$ROLES$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-server-urls.label'),
                            icon: 'd-icon-dollar',
                            expression: '$SERVERURL$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-time.label'),
                            icon: 'd-icon-dollar',
                            expression: '$TIME$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-user.label'),
                            icon: 'd-icon-dollar',
                            expression: '$USER$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-user-locale.label'),
                            icon: 'd-icon-dollar',
                            expression: '$LOCALE$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-week-day.label'),
                            icon: 'd-icon-dollar',
                            expression: '$WEEKDAY$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.new-line.label'),
                            icon: 'd-icon-dollar',
                            expression: '$NEWLINE$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.process-designer.keywords.process-correlation-id.label'),
                            icon: 'd-icon-dollar',
                            expression: '$PROCESSCORRELATIONID$'
                        },
                        {
                            label: 'NULL',
                            icon: 'd-icon-dollar',
                            expression: '$NULL$',
                            hidden: true
                        }
                    ]
                },
                functionsDataDictionaryBranch,
                activitiesDataDictionaryBranch,
                errorsDataDictionaryBranch
            ];
        }));
    }
    setActivitiesDataDictionaryBranches(dataDictionaryBranches) {
        this.activitiesDataDictionarySubject.next(chain(dataDictionaryBranches).compact().sortBy('label').value());
    }
    setErrorsDataDictionaryBranches(dataDictionaryBranches) {
        this.errorsDataDictionarySubject.next(chain(dataDictionaryBranches).compact().sortBy('label').value());
    }
    setVariablesDataDictionaryBranch(dataDictionaryBranch) {
        this.variablesDataDictionaryBranchSubject.next(dataDictionaryBranch);
    }
    getFunctionDataDictionaryDescriptors(functionDescriptors) {
        return functionDescriptors.map((functionDescriptor) => ({
            category: functionDescriptor.type,
            description: functionDescriptor.description,
            name: functionDescriptor.displayName,
            parameters: functionDescriptor.inputParams,
            signature: `${functionDescriptor.displayName}(${map$1(functionDescriptor.inputParams, 'name').join(', ')})`
        }));
    }
}
RxProcessDataDictionaryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDataDictionaryService, deps: [{ token: i1.RxDataDictionaryUtils }, { token: i1.RxGlobalCacheService }, { token: i2.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessDataDictionaryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDataDictionaryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDataDictionaryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.RxDataDictionaryUtils }, { type: i1.RxGlobalCacheService }, { type: i2.TranslateService }]; } });

const specialActionNames = {
    connector: 'rx.Connector',
    webRequest: 'rx.WebRequest',
    userTask: 'rx.UserTask',
    jitterbit: 'iPaaS Jitterbit',
    mulesoft: 'iPaaS MuleSoft'
};
const specialActionLegacyNames = {
    connector: 'connector',
    webRequest: 'WebRequest'
};
const processElementResourceTypes = {
    boundaryEvent: 'com.bmc.arsys.rx.services.process.domain.BoundaryEventDefinition',
    callActivity: 'com.bmc.arsys.rx.services.process.domain.CallActivityDefinition',
    endEvent: 'com.bmc.arsys.rx.services.process.domain.EndEventDefinition',
    errorEvent: 'com.bmc.arsys.rx.services.process.domain.ErrorEventDefinition',
    exclusiveGateway: 'com.bmc.arsys.rx.services.process.domain.ExclusiveGatewayDefinition',
    parallelGateway: 'com.bmc.arsys.rx.services.process.domain.ParallelGatewayDefinition',
    processAction: 'com.bmc.arsys.rx.services.process.domain.ServiceTaskDefinition',
    receiveTask: 'com.bmc.arsys.rx.services.process.domain.ReceiveTaskDefinition',
    sequenceFlow: 'com.bmc.arsys.rx.services.process.domain.SequenceFlowDefinition',
    startEvent: 'com.bmc.arsys.rx.services.process.domain.StartEventDefinition',
    subProcess: 'com.bmc.arsys.rx.services.process.domain.SubProcessDefinition',
    textAnnotation: 'com.bmc.arsys.rx.services.process.domain.TextAnnotation',
    textAnnotationAssociation: 'com.bmc.arsys.rx.services.process.domain.TextAnnotationAssociation',
    timerEvent: 'com.bmc.arsys.rx.services.process.domain.TimerEventDefinition',
    userTask: 'com.bmc.arsys.rx.services.process.domain.UserTaskDefinition'
};
const processElementNamesByResourceType = {
    // process event resource types
    [processElementResourceTypes.boundaryEvent]: 'Boundary',
    [processElementResourceTypes.endEvent]: 'End',
    [processElementResourceTypes.errorEvent]: 'Error',
    [processElementResourceTypes.startEvent]: 'Start',
    [processElementResourceTypes.timerEvent]: 'Timer',
    // process element resource types
    [processElementResourceTypes.callActivity]: 'Call Activity',
    [processElementResourceTypes.exclusiveGateway]: 'Exclusive',
    [processElementResourceTypes.parallelGateway]: 'Parallel',
    [processElementResourceTypes.receiveTask]: 'Receive Task',
    [processElementResourceTypes.subProcess]: 'Sub-Process',
    [processElementResourceTypes.textAnnotation]: 'Annotation',
    [processElementResourceTypes.userTask]: 'User Task',
    // rule element resource types
    'com.bmc.arsys.rx.services.rule.domain.CancelProcessInstanceAction': 'Cancel Process',
    'com.bmc.arsys.rx.services.rule.domain.SignalProcessInstanceAction': 'Signal Process',
    'com.bmc.arsys.rx.services.rule.domain.StartProcessInstanceAction': 'Start Process',
    ruleQualificationResourceType: 'Qualification'
};
const standardProcessElementGroups = {
    activities: {
        name: 'Activities',
        priority: 1
    },
    annotations: {
        name: 'Annotations',
        priority: 2
    },
    events: {
        name: 'Events',
        priority: 3
    },
    gateways: {
        name: 'Gateways',
        priority: 4
    },
    platformActions: {
        name: 'Platform actions',
        priority: 5
    },
    default: {
        priority: 6
    }
};
const standardProcessElements = [
    {
        group: standardProcessElementGroups.annotations.name,
        resourceType: processElementResourceTypes.textAnnotation
    },
    {
        group: standardProcessElementGroups.events.name,
        resourceType: processElementResourceTypes.startEvent
    },
    {
        group: standardProcessElementGroups.events.name,
        resourceType: processElementResourceTypes.endEvent
    },
    {
        group: standardProcessElementGroups.events.name,
        resourceType: processElementResourceTypes.boundaryEvent,
        eventResourceType: processElementResourceTypes.timerEvent
    },
    {
        group: standardProcessElementGroups.events.name,
        resourceType: processElementResourceTypes.boundaryEvent,
        eventResourceType: processElementResourceTypes.errorEvent
    },
    {
        group: standardProcessElementGroups.events.name,
        resourceType: processElementResourceTypes.endEvent,
        eventResourceType: processElementResourceTypes.errorEvent
    },
    {
        group: standardProcessElementGroups.activities.name,
        resourceType: processElementResourceTypes.callActivity
    },
    {
        group: standardProcessElementGroups.activities.name,
        resourceType: processElementResourceTypes.processAction,
        actionTypeName: specialActionNames.connector
    },
    {
        group: standardProcessElementGroups.activities.name,
        resourceType: processElementResourceTypes.receiveTask
    },
    {
        group: standardProcessElementGroups.activities.name,
        resourceType: processElementResourceTypes.subProcess
    },
    {
        group: standardProcessElementGroups.activities.name,
        resourceType: processElementResourceTypes.userTask
    },
    {
        group: standardProcessElementGroups.activities.name,
        resourceType: processElementResourceTypes.processAction,
        actionTypeName: specialActionNames.webRequest
    },
    {
        group: standardProcessElementGroups.activities.name,
        resourceType: processElementResourceTypes.processAction,
        actionTypeName: specialActionNames.jitterbit
    },
    {
        group: standardProcessElementGroups.activities.name,
        resourceType: processElementResourceTypes.processAction,
        actionTypeName: specialActionNames.mulesoft
    },
    {
        group: standardProcessElementGroups.gateways.name,
        resourceType: processElementResourceTypes.parallelGateway
    },
    {
        group: standardProcessElementGroups.gateways.name,
        resourceType: processElementResourceTypes.exclusiveGateway
    }
];
const RX_PROCESS_DEFINITION = {
    errorTypes: {
        allBusinessErrors: 'ALL_BUSINESS_ERRORS',
        businessError: 'BUSINESS_ERROR',
        allExceptions: 'ALL_EXCEPTIONS',
        exception: 'EXCEPTION'
    },
    processElementDisplayNames: {
        callActivity: 'Call activity',
        connector: 'Connector',
        endEvent: 'End',
        errorBoundaryEvent: 'Error boundary',
        errorEndEvent: 'Error end',
        exclusiveGateway: 'Exclusive gateway',
        jitterbit: 'iPaaS Jitterbit',
        mulesoft: 'iPaaS Mulesoft',
        parallelGateway: 'Parallel gateway',
        receiveTask: 'Receive task',
        sequenceFlow: 'Sequence flow',
        startEvent: 'Start',
        timerBoundaryEvent: 'Timer',
        subProcess: 'Sub-Process',
        userTask: 'User task',
        webRequest: 'Web request'
    },
    processElementNamesByResourceType,
    processElementResourceTypes,
    specialActionNames,
    specialActionLegacyNames,
    processElementModelTypes: {
        callActivity: 'rx.CallActivity',
        connector: 'rx.Connector',
        endEvent: 'rx.EndEvent',
        errorBoundaryEvent: 'rx.ErrorBoundaryEvent',
        errorEndEvent: 'rx.ErrorEndEvent',
        exclusiveGateway: 'rx.ExclusiveGateway',
        jitterbit: 'rx.Jitterbit',
        parallelGateway: 'rx.ParallelGateway',
        processAction: 'rx.ProcessAction',
        processActions: 'rx.ProcessActions',
        receiveTask: 'rx.ReceiveTask',
        sequenceFlow: 'rx.SequenceFlow',
        startEvent: 'rx.StartEvent',
        subProcess: 'rx.SubProcess',
        textAnnotation: 'rx.TextAnnotation',
        textAnnotationAssociation: 'rx.TextAnnotationAssociation',
        timerEvent: 'rx.TimerEvent',
        userTask: 'rx.UserTask',
        webRequest: 'rx.WebRequest'
    },
    processElementGroups: [
        standardProcessElementGroups.activities,
        standardProcessElementGroups.annotations,
        standardProcessElementGroups.events,
        standardProcessElementGroups.gateways,
        standardProcessElementGroups.platformActions
    ],
    standardProcessElements,
    standardProcessElementGroups,
    processElementConfigurationProperties: [
        'actionTypeName',
        'calledProcessDefinitionName',
        'eventResourceType',
        'resourceType'
    ],
    processVariableSubTypes: {
        attachment: 11,
        character: 4
    },
    processVariableTypes: {
        record: 'com.bmc.arsys.rx.services.process.domain.record.RecordInstanceFieldType',
        default: 'com.bmc.arsys.rx.services.record.domain.DefaultFieldType'
    },
    runAsUser: {
        administrator: {
            definitionValue: false,
            modelValue: 'administrator'
        },
        currentUser: {
            definitionValue: true,
            modelValue: 'currentUser'
        },
        inheritFromProcess: {
            definitionValue: null,
            modelValue: 'inheritFromProcess'
        }
    },
    timerTypes: {
        interval: 'Interval',
        expression: 'Expression'
    },
    layoutVersion: 2
};

class RxProcessDefinitionService {
    constructor(httpClient, rxGuidService, rxIdService, rxCommandFactoryService) {
        this.httpClient = httpClient;
        this.rxGuidService = rxGuidService;
        this.rxIdService = rxIdService;
        this.rxCommandFactoryService = rxCommandFactoryService;
    }
    // This is a simplified implementation that does not handle localizable strings.
    // This method is only used by Call Activity where the localizable strings are not needed.
    clone(definition) {
        let processDefinitionClone = cloneDeep(definition);
        processDefinitionClone.localizableStrings = {};
        const idMap = {};
        [...processDefinitionClone.inputParams, ...processDefinitionClone.outputParams].forEach((param) => {
            delete param.lastUpdateTime;
            delete param.lastChangedBy;
            delete param.owner;
        });
        let processDefinitionCloneAsString = JSON.stringify(this.cloneElements(processDefinitionClone, null, idMap));
        forEach(idMap, (value, key) => {
            const expressionRegExp = new RegExp('(activityResults\\.|errors\\.)(' + key + ')([\\.\\}]+)', 'g');
            processDefinitionCloneAsString = processDefinitionCloneAsString.replace(expressionRegExp, '$1' + value + '$3');
        });
        return JSON.parse(processDefinitionCloneAsString);
    }
    create(processDefinition) {
        return this.httpClient.post(this.getUrl(), processDefinition, { observe: 'response' });
    }
    get(processDefinitionName, options) {
        return this.httpClient.get(this.getUrl(processDefinitionName), options);
    }
    getNew() {
        const startEventGuid = this.rxGuidService.generate('rx-');
        const endEventGuid = this.rxGuidService.generate('rx-');
        return of({
            allowOverlay: false,
            artifacts: [],
            contextKeyParam: null,
            description: '',
            flowElements: [
                {
                    description: '',
                    guid: startEventGuid,
                    name: RX_PROCESS_DEFINITION.processElementDisplayNames.startEvent,
                    resourceType: RX_PROCESS_DEFINITION.processElementResourceTypes.startEvent
                },
                {
                    description: '',
                    guid: endEventGuid,
                    name: RX_PROCESS_DEFINITION.processElementDisplayNames.endEvent,
                    resourceType: RX_PROCESS_DEFINITION.processElementResourceTypes.endEvent
                }
            ],
            guid: this.rxGuidService.generate('rx-'),
            inputParams: [],
            isEnabled: true,
            lastChangedBy: null,
            lastUpdateTime: null,
            layout: JSON.stringify({
                cells: [
                    {
                        id: this.rxIdService.getBase(startEventGuid),
                        attrs: {
                            '.label': {
                                fill: 'gray',
                                text: ''
                            }
                        },
                        position: {
                            x: 30,
                            y: 385
                        },
                        size: {
                            width: 30,
                            height: 30
                        },
                        type: RX_PROCESS_DEFINITION.processElementModelTypes.startEvent,
                        z: 1
                    },
                    {
                        id: this.rxIdService.getBase(endEventGuid),
                        attrs: {
                            '.label': {
                                fill: 'gray',
                                text: ''
                            }
                        },
                        position: {
                            x: 850,
                            y: 385
                        },
                        size: {
                            width: 30,
                            height: 30
                        },
                        type: RX_PROCESS_DEFINITION.processElementModelTypes.endEvent,
                        z: 2
                    }
                ],
                version: RX_PROCESS_DEFINITION.layoutVersion
            }),
            localVariables: [],
            localizableStrings: {},
            name: '',
            outputParams: [],
            overlayDescriptor: null,
            overlayGroupId: null,
            owner: null,
            permissions: [],
            runAsUser: false,
            scope: RX_BUNDLE.definitionScopeTypes.bundle,
            synchronous: null
        });
    }
    getInputParams(processDefinitionName, options) {
        return this.httpClient.get(`/api/rx/application/process/processdefinition/${encodeURIComponent(processDefinitionName)}/inputParams`, options);
    }
    getOutputParams(processDefinitionName, options) {
        return this.httpClient.get(`/api/rx/application/process/processdefinition/${encodeURIComponent(processDefinitionName)}/outputParams`, options);
    }
    isArtifact(flowElementOrArtifact) {
        return (flowElementOrArtifact.resourceType === RX_PROCESS_DEFINITION.processElementResourceTypes.textAnnotation ||
            flowElementOrArtifact.resourceType === RX_PROCESS_DEFINITION.processElementResourceTypes.textAnnotationAssociation);
    }
    isBoundaryEvent(flowElementOrArtifact) {
        return flowElementOrArtifact.resourceType === RX_PROCESS_DEFINITION.processElementResourceTypes.boundaryEvent;
    }
    isErrorEvent(flowElementOrArtifact) {
        return (flowElementOrArtifact.event?.resourceType ===
            RX_PROCESS_DEFINITION.processElementResourceTypes.errorEvent);
    }
    isFlowElement(flowElementOrArtifact) {
        return !this.isArtifact(flowElementOrArtifact);
    }
    isProcessAction(flowElementOrArtifact) {
        return flowElementOrArtifact.resourceType === RX_PROCESS_DEFINITION.processElementResourceTypes.processAction;
    }
    isSubProcess(flowElementOrArtifact) {
        return flowElementOrArtifact.resourceType === RX_PROCESS_DEFINITION.processElementResourceTypes.subProcess;
    }
    normalizeModelType(modelType) {
        return modelType.replace(':', '.');
    }
    revertCustomization(processDefinitionName) {
        const revertCustomizationCommand = this.rxCommandFactoryService.forResourceType('com.bmc.arsys.rx.application.process.command.RevertProcessDefinitionCommand');
        return revertCustomizationCommand.execute({ processDefinitionName });
    }
    update(processDefinition, options) {
        return this.httpClient.put(this.getUrl(processDefinition.name), processDefinition, options);
    }
    cloneElements(definition, parentDefinition, idMap) {
        [...definition.artifacts, ...definition.flowElements].forEach((element) => {
            const layoutRegExp = new RegExp('("id":"|"parent":")(' + this.rxIdService.getBase(element.guid) + ')"', 'g');
            idMap[element.guid] = idMap[element.guid] || this.rxIdService.generate();
            element.originalGuid = element.guid;
            element.guid = idMap[element.guid];
            delete element.lastUpdateTime;
            delete element.lastChangedBy;
            delete element.owner;
            if (definition.layout) {
                definition.layout = definition.layout.replace(layoutRegExp, '$1' + this.rxIdService.getBase(element.guid) + '"');
            }
            if (parentDefinition?.layout) {
                parentDefinition.layout = parentDefinition.layout.replace(layoutRegExp, '$1' + this.rxIdService.getBase(element.guid) + '"');
            }
            const boundaryEvent = element;
            if (boundaryEvent.attachedToNode) {
                idMap[boundaryEvent.attachedToNode] = idMap[boundaryEvent.attachedToNode] || this.rxIdService.generate();
                boundaryEvent.attachedToNode = idMap[boundaryEvent.attachedToNode];
            }
            const sequenceFlow = element;
            if (sequenceFlow.sourceNode) {
                idMap[sequenceFlow.sourceNode] = idMap[sequenceFlow.sourceNode] || this.rxIdService.generate();
                idMap[sequenceFlow.targetNode] = idMap[sequenceFlow.targetNode] || this.rxIdService.generate();
                sequenceFlow.sourceNode = idMap[sequenceFlow.sourceNode];
                sequenceFlow.targetNode = idMap[sequenceFlow.targetNode];
            }
            const subProcessElement = element;
            if (subProcessElement.resourceType === RX_PROCESS_DEFINITION.processElementResourceTypes.subProcess) {
                if (subProcessElement.layout) {
                    subProcessElement.layout = subProcessElement.layout.replace(layoutRegExp, '$1' + this.rxIdService.getBase(element.guid) + '"');
                }
                this.cloneElements(subProcessElement, definition, idMap);
            }
        });
        return definition;
    }
    getUrl(processDefinitionName) {
        return processDefinitionName
            ? `/api/rx/application/process/processdefinition/${encodeURIComponent(processDefinitionName)}`
            : '/api/rx/application/process/processdefinition';
    }
}
RxProcessDefinitionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDefinitionService, deps: [{ token: i1$1.HttpClient }, { token: i2$1.RxGuidService }, { token: i2$1.RxIdService }, { token: i1.RxCommandFactoryService }], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessDefinitionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDefinitionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDefinitionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }, { type: i2$1.RxGuidService }, { type: i2$1.RxIdService }, { type: i1.RxCommandFactoryService }]; } });

var ListFieldDefinitionSubType;
(function (ListFieldDefinitionSubType) {
    ListFieldDefinitionSubType[ListFieldDefinitionSubType["Attachment"] = 11] = "Attachment";
    ListFieldDefinitionSubType[ListFieldDefinitionSubType["Character"] = 4] = "Character";
})(ListFieldDefinitionSubType || (ListFieldDefinitionSubType = {}));

class RxProcessElementSearchService {
    find(definition, predicate) {
        let processElement = this.findElementInDefinition(definition, predicate);
        if (!processElement) {
            processElement = find(this.flattenElements(definition), predicate);
        }
        return processElement;
    }
    findElementInDefinition(definition, predicate) {
        return find(definition.flowElements, predicate) || find(definition.artifacts, predicate);
    }
    findOwner(definition, guid) {
        const processElement = this.findElementInDefinition(definition, { guid });
        let result;
        if (processElement) {
            result = definition;
        }
        else {
            result = find(this.flattenElements(definition), (element) => element.resourceType === RX_PROCESS_DEFINITION.processElementResourceTypes.subProcess
                ? this.findElementInDefinition(element, { guid })
                : undefined);
        }
        return result;
    }
    findByGuid(definition, guid) {
        return this.find(definition, { guid });
    }
    filter(definition, predicate) {
        return filter(this.flattenElements(definition), predicate);
    }
    forEach(definition, iteratee) {
        return forEach(this.flattenElements(definition), iteratee);
    }
    flattenElements(definition) {
        return transform([].concat(definition.flowElements || [], definition.artifacts || []), (result, element) => {
            result.push(element);
            if (element.resourceType === RX_PROCESS_DEFINITION.processElementResourceTypes.subProcess) {
                result.push(...this.flattenElements(element));
            }
        }, []);
    }
}
RxProcessElementSearchService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessElementSearchService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessElementSearchService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessElementSearchService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessElementSearchService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

const processDefinitionDataPageQuery$1 = 'com.bmc.arsys.rx.application.process.datapage.ProcessDefinitionDataPageQuery';
class RxProcessDefinitionDataPageService extends DataPage {
    constructor(injector) {
        super(injector, processDefinitionDataPageQuery$1);
        this.injector = injector;
    }
}
RxProcessDefinitionDataPageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDefinitionDataPageService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessDefinitionDataPageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDefinitionDataPageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDefinitionDataPageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

const processDefinitionDataPageQuery = 'com.bmc.arsys.rx.application.process.datapage.ProcessDefinitionErrorDescriptorDataPageQuery';
class RxProcessDefinitionErrorDataPageService extends DataPage {
    constructor(injector) {
        super(injector, processDefinitionDataPageQuery);
        this.injector = injector;
    }
}
RxProcessDefinitionErrorDataPageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDefinitionErrorDataPageService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessDefinitionErrorDataPageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDefinitionErrorDataPageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDefinitionErrorDataPageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

class RxProcessDefinitionCacheService {
    constructor(errorHandler, rxCurrentUserService, rxProcessDefinitionDataPageService, rxProcessDefinitionErrorDataPageService, rxProcessDefinitionService, rxProcessElementSearchService) {
        this.errorHandler = errorHandler;
        this.rxCurrentUserService = rxCurrentUserService;
        this.rxProcessDefinitionDataPageService = rxProcessDefinitionDataPageService;
        this.rxProcessDefinitionErrorDataPageService = rxProcessDefinitionErrorDataPageService;
        this.rxProcessDefinitionService = rxProcessDefinitionService;
        this.rxProcessElementSearchService = rxProcessElementSearchService;
        this.allProcessDefinitionNames = null;
        this.businessErrorDefinitions = new Map();
        this.consumers = new Set();
        this.processDefinitions = new Map();
        this.processDefinitionNames = null;
        this.processDefinitionOutputParams = new Map();
    }
    clearCache(processDefinitionNames) {
        if (processDefinitionNames) {
            forEach(processDefinitionNames, (processDefinitionName) => {
                this.processDefinitions.delete(processDefinitionName);
                this.processDefinitionOutputParams.delete(processDefinitionName);
            });
        }
        else {
            this.businessErrorDefinitions.clear();
            this.processDefinitions.clear();
            this.processDefinitionOutputParams.clear();
        }
    }
    findProcessDefinition(processDefinitionName) {
        return this.getProcessDefinition(processDefinitionName).pipe(catchError((error) => {
            this.errorHandler.handleError(error);
            return of(null);
        }));
    }
    getAllProcessDefinitionNames() {
        if (!this.allProcessDefinitionNames) {
            this.allProcessDefinitionNames = this.rxProcessDefinitionDataPageService
                .get({
                headers: {
                    'default-bundle-scope': ''
                },
                params: {
                    pageSize: -1,
                    startIndex: 0,
                    propertySelection: ['name']
                }
            })
                .pipe(map((response) => map$1(response.data, 'name')));
        }
        return this.allProcessDefinitionNames;
    }
    getBusinessErrorDefinitions(processDefinitionNames) {
        processDefinitionNames.forEach((processDefinitionName) => {
            if (!this.businessErrorDefinitions.has(processDefinitionName)) {
                this.businessErrorDefinitions.set(processDefinitionName, this.rxProcessDefinitionErrorDataPageService
                    .get({
                    params: { name: encodeURIComponent(processDefinitionName) }
                })
                    .pipe(map((result) => get(result, 'data[0]').errors)));
            }
        });
        return Array.from(this.businessErrorDefinitions.values());
    }
    getOutputParams(processDefinitionName) {
        if (!this.processDefinitionOutputParams.has(processDefinitionName)) {
            const processDefinitionOutputParams$ = this.rxProcessDefinitionService
                .getOutputParams(processDefinitionName, {
                headers: new HttpHeaders({
                    'Design-Time': (this.rxCurrentUserService.isAdministrator() || this.rxCurrentUserService.isBusinessAnalyst()).toString()
                })
            })
                .pipe(shareReplay(1));
            this.processDefinitionOutputParams.set(processDefinitionName, processDefinitionOutputParams$);
        }
        return this.processDefinitionOutputParams.get(processDefinitionName);
    }
    getProcessDefinition(processDefinitionName) {
        if (!this.processDefinitions.has(processDefinitionName)) {
            const processDefinition$ = this.rxProcessDefinitionService
                .get(processDefinitionName, {
                headers: new HttpHeaders({
                    'Design-Time': (this.rxCurrentUserService.isAdministrator() || this.rxCurrentUserService.isBusinessAnalyst()).toString()
                })
            })
                .pipe(shareReplay(1));
            this.processDefinitions.set(processDefinitionName, processDefinition$);
        }
        return this.processDefinitions.get(processDefinitionName);
    }
    getProcessDefinitionNames() {
        if (!this.processDefinitionNames) {
            this.processDefinitionNames = this.rxProcessDefinitionDataPageService
                .get({
                params: {
                    pageSize: -1,
                    startIndex: 0,
                    propertySelection: ['name'],
                    requireDependent: true
                }
            })
                .pipe(map((response) => map$1(response.data, 'name')));
        }
        return this.processDefinitionNames;
    }
    getProcessDefinitions(processDefinitionNames) {
        return isEmpty(processDefinitionNames)
            ? of([])
            : this.getAllProcessDefinitionNames().pipe(switchMap((processDefinitionNamesList) => {
                // exclude dynamic process definition Names (expressions)
                const validProcessDefinitionNames = filter(processDefinitionNames, (processDefinitionName) => includes(processDefinitionNamesList, processDefinitionName));
                // exclude names of processes already cached
                const namesToRequest = reject(validProcessDefinitionNames, (processDefinitionName) => isPlainObject(this.processDefinitions.get(processDefinitionName)));
                return isEmpty(namesToRequest)
                    ? this.getProcessDefinitionsFromCache(processDefinitionNames)
                    : forkJoin(namesToRequest.map((processDefinitionName) => this.getProcessDefinition(processDefinitionName))).pipe(switchMap(() => this.getProcessDefinitionsFromCache(namesToRequest)));
            }));
    }
    loadProcessDefinitionTree(processDefinitions) {
        let definitions = [];
        if (isArray(processDefinitions)) {
            definitions = [...processDefinitions];
        }
        else {
            definitions = [processDefinitions];
        }
        const processDefinitionNames = chain(definitions)
            .compact()
            .map((processDefinition) => chain(this.rxProcessElementSearchService.filter(processDefinition, 'calledProcessDefinitionName'))
            .map('calledProcessDefinitionName')
            .reject((processDefinitionName) => this.processDefinitions.has(processDefinitionName))
            .reject((processDefinitionName) => Boolean(processDefinitionName?.match(/\$\{.*}/)))
            .value())
            .flatten()
            .uniq()
            .value();
        return isEmpty(processDefinitionNames) ? of(null) : this.getProcessDefinitions(processDefinitionNames);
    }
    registerConsumer(consumerDestroy$) {
        this.consumers.add(consumerDestroy$);
        consumerDestroy$.subscribe(() => {
            this.consumers.delete(consumerDestroy$);
            if (isEmpty(this.consumers)) {
                this.clearCache();
            }
        });
    }
    setProcessDefinition(processDefinition) {
        this.processDefinitions.set(processDefinition.lastUpdateTime ? processDefinition.name : processDefinition.guid, of(processDefinition));
    }
    getProcessDefinitionsFromCache(processDefinitionNames) {
        return forkJoin(compact(processDefinitionNames.map((processDefinitionName) => this.processDefinitions.get(processDefinitionName))));
    }
}
RxProcessDefinitionCacheService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDefinitionCacheService, deps: [{ token: i0.ErrorHandler }, { token: i1.RxCurrentUserService }, { token: RxProcessDefinitionDataPageService }, { token: RxProcessDefinitionErrorDataPageService }, { token: RxProcessDefinitionService }, { token: RxProcessElementSearchService }], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessDefinitionCacheService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDefinitionCacheService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDefinitionCacheService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.ErrorHandler }, { type: i1.RxCurrentUserService }, { type: RxProcessDefinitionDataPageService }, { type: RxProcessDefinitionErrorDataPageService }, { type: RxProcessDefinitionService }, { type: RxProcessElementSearchService }]; } });

var ElementVisibilityOptionsOld;
(function (ElementVisibilityOptionsOld) {
    ElementVisibilityOptionsOld["Always"] = "Always";
    ElementVisibilityOptionsOld["Never"] = "Never";
})(ElementVisibilityOptionsOld || (ElementVisibilityOptionsOld = {}));

class RxProcessElementRegistryService {
    constructor(rxBundleCacheService, rxActionTypeUtilsService, rxGlobalCacheService, rxLogService, rxNotificationService, rxProcessDefinitionService) {
        this.rxBundleCacheService = rxBundleCacheService;
        this.rxActionTypeUtilsService = rxActionTypeUtilsService;
        this.rxGlobalCacheService = rxGlobalCacheService;
        this.rxLogService = rxLogService;
        this.rxNotificationService = rxNotificationService;
        this.rxProcessDefinitionService = rxProcessDefinitionService;
        this.baseCallActivity = null;
        this.baseServerAction = null;
        this.callActivityDescriptors = new Map();
        this.callActivityOwnerBundleIds = {};
        this.cellNamespaceValue = {};
        this.cellViewNamespaceValue = {};
        this.serverActionDescriptors = new Map();
        this.serverActionOwnerBundleIds = {};
        this.specialActionDescriptors = new Map();
        this.standardElementDescriptors = new Map();
        this.unresolvedCallActivityDescriptors = [];
        this.unresolvedServerActionDescriptors = [];
        this.callActivityOwnerBundleIds$ = this.rxGlobalCacheService.getBundleDescriptors().pipe(map((bundleDescriptors) => {
            bundleDescriptors.unshift(...remove(bundleDescriptors, { id: RX_APPLICATION.platformBundleId }));
            return bundleDescriptors.reduce((ownerBundleIds, bundleDescriptor) => {
                bundleDescriptor.uiOptions.callActivities?.forEach((calledProcessDefinitionName) => {
                    if (ownerBundleIds.hasOwnProperty(calledProcessDefinitionName)) {
                        this.rxLogService.warning(`[Process Element Registry] ${calledProcessDefinitionName} is already provided by ${ownerBundleIds[calledProcessDefinitionName]}.`);
                    }
                    else {
                        ownerBundleIds[calledProcessDefinitionName] = bundleDescriptor.id;
                    }
                });
                return ownerBundleIds;
            }, this.callActivityOwnerBundleIds);
        }), shareReplay(1));
        this.unresolvedCallActivityDescriptors$ = this.callActivityOwnerBundleIds$.pipe(switchMap((ownerBundleIds) => Promise.allSettled(this.unresolvedCallActivityDescriptors).then((descriptors) => {
            descriptors.forEach((result) => {
                if (result.status === 'fulfilled') {
                    const descriptor = result.value;
                    if (ownerBundleIds[descriptor.calledProcessDefinitionName]) {
                        this.rxLogService.debug(`[Process Element Registry] Call activity ${descriptor.calledProcessDefinitionName} has a custom implementation provided by ${ownerBundleIds[descriptor.calledProcessDefinitionName]}.`);
                    }
                    this.registerCallActivitySync(descriptor);
                }
                else {
                    this.rxLogService.error(`[Process Element Registry] Call activity could not be registered. ${result.reason}`);
                }
            });
            return this.callActivityDescriptors;
        })), tap(() => {
            this.unresolvedCallActivityDescriptors.splice(0, this.unresolvedCallActivityDescriptors.length);
        }));
        this.serverActionOwnerBundleIds$ = this.rxGlobalCacheService.getBundleDescriptors().pipe(map((bundleDescriptors) => {
            bundleDescriptors.unshift(...remove(bundleDescriptors, { id: RX_APPLICATION.platformBundleId }));
            return bundleDescriptors.reduce((ownerBundleIds, bundleDescriptor) => {
                bundleDescriptor.uiOptions.serverActions?.forEach((serverActionName) => {
                    if (ownerBundleIds.hasOwnProperty(serverActionName)) {
                        this.rxLogService.warning(`[Process Element Registry] Server action ${serverActionName} is already provided by ${ownerBundleIds[serverActionName]}.`);
                    }
                    else {
                        ownerBundleIds[serverActionName] = bundleDescriptor.id;
                    }
                });
                return ownerBundleIds;
            }, this.serverActionOwnerBundleIds);
        }), shareReplay(1));
        this.unresolvedServerActionDescriptors$ = this.serverActionOwnerBundleIds$.pipe(switchMap((ownerBundleIds) => Promise.allSettled(this.unresolvedServerActionDescriptors).then((descriptors) => {
            descriptors.forEach((result) => {
                if (result.status === 'fulfilled') {
                    const descriptor = result.value;
                    if (ownerBundleIds[descriptor.actionTypeName]) {
                        this.rxLogService.debug(`[Process Element Registry] Server action ${descriptor.actionTypeName} has a custom implementation provided by ${ownerBundleIds[descriptor.actionTypeName]}.`);
                    }
                    this.registerServerActionSync(descriptor);
                }
                else {
                    this.rxLogService.error(`[Process Element Registry] Server action could not be registered. ${result.reason}`);
                }
            });
            return this.serverActionDescriptors;
        })), tap(() => {
            this.unresolvedServerActionDescriptors.splice(0, this.unresolvedServerActionDescriptors.length);
        }));
    }
    get cellNamespace() {
        return this.cellNamespaceValue;
    }
    get cellViewNamespace() {
        return this.cellViewNamespaceValue;
    }
    getBaseCallActivity() {
        return this.baseCallActivity;
    }
    getBaseServerAction() {
        return this.baseServerAction;
    }
    getCallActivities() {
        return [...this.callActivityDescriptors.values()];
    }
    getCallActivity(calledProcessDefinitionName) {
        return (this.callActivityDescriptors.get(calledProcessDefinitionName) ??
            this.baseCallActivity);
    }
    getCalledProcessDefinitionName(modelType) {
        return last(modelType.split(`${RX_PROCESS_DEFINITION.processElementModelTypes.callActivity}.`));
    }
    getCalledProcessDefinitionNames() {
        return [...this.callActivityDescriptors.keys()];
    }
    getElementDisplayNameByModelType(modelType) {
        let displayName = null;
        if (this.isStandardAction(modelType) || this.isSpecialAction(modelType)) {
            const actionName = this.getServerActionName(modelType);
            const descriptor = this.getServerAction(actionName);
            displayName =
                descriptor.displayName ||
                    this.rxBundleCacheService.getActionTypeSync(actionName)?.displayName ||
                    this.rxActionTypeUtilsService.prettifyActionTypeName(actionName);
        }
        else if (modelType === RX_PROCESS_DEFINITION.processElementModelTypes.callActivity ||
            modelType.startsWith(`${RX_PROCESS_DEFINITION.processElementModelTypes.callActivity}.`)) {
            const calledProcessDefinitionName = this.getCalledProcessDefinitionName(modelType);
            const descriptor = this.getCallActivity(calledProcessDefinitionName) ?? this.baseCallActivity;
            displayName = descriptor.displayName;
        }
        else {
            displayName = this.getStandardElement(modelType).displayName;
        }
        return displayName;
    }
    getElementServiceByModelType(modelType) {
        let service = null;
        if (this.isStandardAction(modelType) || this.isSpecialAction(modelType)) {
            const actionName = this.getServerActionName(modelType);
            const descriptor = this.specialActionDescriptors.get(actionName) ?? this.getServerAction(actionName) ?? this.baseServerAction;
            service = descriptor.service;
        }
        else if (modelType === RX_PROCESS_DEFINITION.processElementModelTypes.callActivity ||
            modelType.startsWith(`${RX_PROCESS_DEFINITION.processElementModelTypes.callActivity}.`)) {
            const calledProcessDefinitionName = this.getCalledProcessDefinitionName(modelType);
            const descriptor = this.getCallActivity(calledProcessDefinitionName) ?? this.baseCallActivity;
            service = descriptor.service;
        }
        else {
            service = this.getStandardElement(modelType).service;
        }
        return service;
    }
    getServerAction(actionName) {
        return (this.specialActionDescriptors.get(actionName) ??
            this.serverActionDescriptors.get(actionName) ??
            this.baseServerAction);
    }
    getServerActionName(modelType) {
        return last(modelType.split(`${RX_PROCESS_DEFINITION.processElementModelTypes.processActions}.`));
    }
    getServerActionNames() {
        return [...this.serverActionDescriptors.keys()];
    }
    getServerActions() {
        return [...this.serverActionDescriptors.values()];
    }
    getSpecialActions() {
        return [...this.specialActionDescriptors.values()];
    }
    getStandardElement(modelType) {
        return this.standardElementDescriptors.get(modelType);
    }
    getStandardElements() {
        return [...this.standardElementDescriptors.values()];
    }
    registerBaseCallActivity(baseCallActivity) {
        if (!this.baseCallActivity) {
            this.baseCallActivity = baseCallActivity;
            set(this.cellNamespaceValue, RX_PROCESS_DEFINITION.processElementModelTypes.callActivity, this.baseCallActivity.model);
            set(this.cellViewNamespaceValue, `${RX_PROCESS_DEFINITION.processElementModelTypes.callActivity}View`, this.baseCallActivity.viewModel);
        }
    }
    registerBaseServerAction(baseServerAction) {
        if (!this.baseServerAction) {
            this.baseServerAction = baseServerAction;
            set(this.cellNamespaceValue, RX_PROCESS_DEFINITION.processElementModelTypes.processAction, this.baseServerAction.model);
            set(this.cellViewNamespaceValue, `${RX_PROCESS_DEFINITION.processElementModelTypes.processAction}View`, this.baseServerAction.viewModel);
        }
    }
    registerCallActivity(descriptor) {
        this.unresolvedCallActivityDescriptors.push(Promise.resolve(descriptor));
    }
    registerServerAction(descriptor) {
        this.unresolvedServerActionDescriptors.push(Promise.resolve(descriptor));
    }
    registerSpecialAction(descriptor) {
        set(this.cellNamespaceValue, descriptor.actionTypeName, descriptor.model);
        set(this.cellViewNamespaceValue, `${descriptor.actionTypeName}View`, descriptor.viewModel);
        this.specialActionDescriptors.set(descriptor.actionTypeName, descriptor);
    }
    registerStandardElement(descriptor) {
        set(this.cellNamespaceValue, descriptor.modelType, descriptor.model);
        set(this.cellViewNamespaceValue, `${descriptor.modelType}View`, descriptor.viewModel);
        this.standardElementDescriptors.set(descriptor.modelType, descriptor);
    }
    isRegisteredStandardAction(modelType) {
        return (modelType.startsWith(RX_PROCESS_DEFINITION.processElementModelTypes.processActions) &&
            !this.isSpecialAction(modelType));
    }
    isSpecialAction(modelType) {
        return [
            RX_PROCESS_DEFINITION.specialActionNames.connector,
            RX_PROCESS_DEFINITION.specialActionNames.userTask,
            RX_PROCESS_DEFINITION.specialActionNames.webRequest
        ].includes(modelType);
    }
    isStandardAction(modelType) {
        return this.isRegisteredStandardAction(modelType) || this.isUnregisteredStandardAction(modelType);
    }
    isUnregisteredStandardAction(modelType) {
        return modelType === RX_PROCESS_DEFINITION.processElementModelTypes.processAction;
    }
    registerCallActivitySync(descriptor) {
        const modelType = this.rxProcessDefinitionService.normalizeModelType(`${RX_PROCESS_DEFINITION.processElementModelTypes.callActivity}.${descriptor.calledProcessDefinitionName}`);
        set(this.cellNamespaceValue, modelType, descriptor.model || this.baseCallActivity.model);
        set(this.cellViewNamespaceValue, `${modelType}View`, this.baseCallActivity.viewModel);
        this.callActivityDescriptors.set(descriptor.calledProcessDefinitionName, descriptor);
    }
    registerServerActionSync(descriptor) {
        const modelType = this.rxProcessDefinitionService.normalizeModelType(`${RX_PROCESS_DEFINITION.processElementModelTypes.processActions}.${descriptor.actionTypeName}`);
        set(this.cellNamespaceValue, modelType, descriptor.model || this.baseServerAction.model);
        set(this.cellViewNamespaceValue, `${modelType}View`, this.baseServerAction.viewModel);
        this.serverActionDescriptors.set(descriptor.actionTypeName, descriptor);
    }
}
RxProcessElementRegistryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessElementRegistryService, deps: [{ token: i1.RxBundleCacheService }, { token: i1.RxActionTypeUtilsService }, { token: i1.RxGlobalCacheService }, { token: i1.RxLogService }, { token: i1.RxNotificationService }, { token: RxProcessDefinitionService }], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessElementRegistryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessElementRegistryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessElementRegistryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.RxBundleCacheService }, { type: i1.RxActionTypeUtilsService }, { type: i1.RxGlobalCacheService }, { type: i1.RxLogService }, { type: i1.RxNotificationService }, { type: RxProcessDefinitionService }]; } });

var ElementVisibilityOptions;
(function (ElementVisibilityOptions) {
    ElementVisibilityOptions["Always"] = "Always";
    ElementVisibilityOptions["Never"] = "Never";
})(ElementVisibilityOptions || (ElementVisibilityOptions = {}));

class RxProcessElementsService {
    constructor(rxActionTypeUtilsService, rxDefinitionNameService, rxProcessElementRegistryService) {
        this.rxActionTypeUtilsService = rxActionTypeUtilsService;
        this.rxDefinitionNameService = rxDefinitionNameService;
        this.rxProcessElementRegistryService = rxProcessElementRegistryService;
    }
    getPaletteItemKey(value) {
        return `${value.actionTypeName};${value.resourceType};${value.eventResourceType};${value.calledProcessDefinitionName}`;
    }
    buildPaletteGroups(actionTypes, bundleDescriptors) {
        const processElementItems = this.rxProcessElementRegistryService
            .getStandardElements()
            .filter((descriptor) => descriptor.visibility !== ElementVisibilityOptions.Never)
            .map((descriptor) => {
            const item = {
                group: descriptor.group,
                label: descriptor.displayName,
                layout: descriptor.paletteItemLayout,
                value: {
                    displayName: descriptor.displayName,
                    key: null,
                    resourceType: descriptor.resourceType,
                    type: descriptor.modelType
                }
            };
            if ([
                RX_PROCESS_DEFINITION.processElementModelTypes.callActivity,
                RX_PROCESS_DEFINITION.processElementModelTypes.subProcess,
                RX_PROCESS_DEFINITION.processElementModelTypes.receiveTask
            ].includes(descriptor.modelType)) {
                item.value.label = descriptor.displayName;
            }
            if (descriptor.modelType === RX_PROCESS_DEFINITION.processElementModelTypes.timerEvent) {
                item.value.eventResourceType = RX_PROCESS_DEFINITION.processElementResourceTypes.timerEvent;
            }
            else if (descriptor.modelType === RX_PROCESS_DEFINITION.processElementModelTypes.errorBoundaryEvent ||
                descriptor.modelType === RX_PROCESS_DEFINITION.processElementModelTypes.errorEndEvent) {
                item.value.eventResourceType = RX_PROCESS_DEFINITION.processElementResourceTypes.errorEvent;
            }
            return item;
        });
        const specialActionItems = this.rxProcessElementRegistryService
            .getSpecialActions()
            .filter((descriptor) => descriptor.visibility !== ElementVisibilityOptions.Never)
            .map((descriptor) => ({
            group: descriptor.group,
            label: descriptor.displayName,
            layout: {
                border: RX_DESIGNER.paletteItemBorderTypes.solid,
                labelPosition: RX_DESIGNER.paletteItemLabelPositions.inner,
                shape: RX_DESIGNER.paletteItemShapes.rectangle,
                icon: {
                    path: descriptor.icon,
                    position: RX_DESIGNER.paletteIconPositions.top
                }
            },
            value: {
                actionTypeName: descriptor.actionTypeName,
                displayName: descriptor.displayName,
                key: null,
                label: descriptor.displayName,
                resourceType: RX_PROCESS_DEFINITION.processElementResourceTypes.processAction
            }
        }));
        const callActivityItems = this.rxProcessElementRegistryService
            .getCallActivities()
            .filter((descriptor) => find(bundleDescriptors, {
            id: this.rxDefinitionNameService.getBundleId(descriptor.calledProcessDefinitionName)
        }))
            .filter((descriptor) => descriptor.visibility !== ElementVisibilityOptions.Never)
            .map((descriptor) => {
            const bundleDescriptor = find(bundleDescriptors, {
                id: this.rxDefinitionNameService.getBundleId(descriptor.calledProcessDefinitionName)
            });
            const displayName = descriptor.displayName || this.rxDefinitionNameService.getDisplayName(descriptor.calledProcessDefinitionName);
            return {
                group: bundleDescriptor.friendlyName,
                label: displayName,
                layout: {
                    border: RX_DESIGNER.paletteItemBorderTypes.bold,
                    labelPosition: RX_DESIGNER.paletteItemLabelPositions.inner,
                    shape: RX_DESIGNER.paletteItemShapes.rectangle,
                    icon: {
                        path: RX_DESIGNER_ELEMENT_SHAPE.bpmnIcons.cross,
                        position: RX_DESIGNER.paletteIconPositions.bottom
                    }
                },
                value: {
                    calledProcessDefinitionName: descriptor.calledProcessDefinitionName,
                    displayName,
                    key: null,
                    label: displayName,
                    resourceType: RX_PROCESS_DEFINITION.processElementResourceTypes.callActivity
                }
            };
        });
        const serverActionItems = actionTypes.map((actionType) => {
            const descriptor = this.rxProcessElementRegistryService.getServerAction(actionType.actionTypeName);
            const displayName = descriptor.displayName ||
                actionType.displayName ||
                this.rxActionTypeUtilsService.prettifyActionTypeName(actionType.actionTypeName);
            return {
                group: this.rxActionTypeUtilsService.getActionGroupName(bundleDescriptors, actionType),
                label: displayName,
                layout: {
                    border: RX_DESIGNER.paletteItemBorderTypes.solid,
                    labelPosition: RX_DESIGNER.paletteItemLabelPositions.inner,
                    shape: RX_DESIGNER.paletteItemShapes.rectangle,
                    icon: {
                        path: RX_DESIGNER_ELEMENT_SHAPE.bpmnIcons.gear,
                        position: RX_DESIGNER.paletteIconPositions.top
                    }
                },
                value: {
                    actionTypeName: descriptor.actionTypeName ?? actionType.actionTypeName,
                    displayName,
                    key: null,
                    label: displayName,
                    resourceType: RX_PROCESS_DEFINITION.processElementResourceTypes.processAction,
                    type: RX_PROCESS_DEFINITION.processElementModelTypes.processAction
                }
            };
        });
        return chain([...processElementItems, ...specialActionItems, ...callActivityItems, ...serverActionItems])
            .orderBy((item) => item.label.toLowerCase())
            .forEach((item) => {
            item.value.key = this.getPaletteItemKey({
                default: false,
                actionTypeName: item.value.actionTypeName,
                resourceType: item.value.resourceType,
                calledProcessDefinitionName: item.value.calledProcessDefinitionName,
                eventResourceType: item.value.eventResourceType
            });
        })
            .reduce((groups, item) => {
            const group = find(groups, { label: item.group });
            if (group) {
                group.children.push(item);
            }
            else {
                groups.push({
                    label: item.group,
                    children: [item]
                });
            }
            return groups;
        }, [])
            .sortBy('label')
            .sortBy((item) => {
            const group = find(RX_PROCESS_DEFINITION.processElementGroups, { name: item.label });
            return group?.priority ?? RX_PROCESS_DEFINITION.standardProcessElementGroups.default.priority;
        })
            .value();
    }
    toLegacyFormat(preferences) {
        if (preferences.actionTypeName === RX_PROCESS_DEFINITION.specialActionNames.connector) {
            preferences.actionTypeName = RX_PROCESS_DEFINITION.specialActionLegacyNames.connector;
        }
        else if (preferences.actionTypeName === RX_PROCESS_DEFINITION.specialActionNames.webRequest) {
            preferences.actionTypeName = RX_PROCESS_DEFINITION.specialActionLegacyNames.webRequest;
        }
        else if (preferences.actionTypeName === RX_PROCESS_DEFINITION.specialActionNames.userTask) {
            delete preferences.actionTypeName;
            preferences.resourceType = RX_PROCESS_DEFINITION.processElementResourceTypes.userTask;
        }
        return preferences;
    }
    fromLegacyFormat(preferences) {
        if (preferences.actionTypeName === RX_PROCESS_DEFINITION.specialActionLegacyNames.connector) {
            preferences.actionTypeName = RX_PROCESS_DEFINITION.specialActionNames.connector;
        }
        else if (preferences.actionTypeName === RX_PROCESS_DEFINITION.specialActionLegacyNames.webRequest) {
            preferences.actionTypeName = RX_PROCESS_DEFINITION.specialActionNames.webRequest;
        }
        else if (preferences.resourceType === RX_PROCESS_DEFINITION.processElementResourceTypes.userTask) {
            preferences.resourceType = RX_PROCESS_DEFINITION.processElementResourceTypes.processAction;
            preferences.actionTypeName = RX_PROCESS_DEFINITION.specialActionNames.userTask;
        }
        return preferences;
    }
}
RxProcessElementsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessElementsService, deps: [{ token: i1.RxActionTypeUtilsService }, { token: i1.RxDefinitionNameService }, { token: RxProcessElementRegistryService }], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessElementsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessElementsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessElementsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.RxActionTypeUtilsService }, { type: i1.RxDefinitionNameService }, { type: RxProcessElementRegistryService }]; } });

const RX_PROCESS_INSTANCE = {
    defaultContextKey: 'instance',
    status: {
        active: 'ACTIVE',
        completed: 'COMPLETED',
        errored: 'ERRORED',
        suspended: 'SUSPENDED'
    },
    variableNames: {
        activityResults: 'Process Instance Activity Results'
    }
};

class RxProcessInstanceService {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    get(processDefinitionName, processInstanceId) {
        return this.httpClient.get(`/api/rx/application/process/processinstance/${encodeURIComponent(processDefinitionName)}/${processInstanceId}`);
    }
    getLog(processDefinitionName, processInstanceId) {
        return this.httpClient.get(`/api/rx/application/process/processinstance/log/${encodeURIComponent(processDefinitionName)}/${processInstanceId}/view`);
    }
    downloadLog(processDefinitionName, processInstanceId) {
        return this.httpClient.get(`/api/rx/application/process/processinstance/log/${encodeURIComponent(processDefinitionName)}/${processInstanceId}/download`, {
            responseType: 'blob'
        });
    }
}
RxProcessInstanceService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceService, deps: [{ token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessInstanceService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }]; } });

class RxProcessInstanceCommandsService {
    constructor(rxCommandFactoryService) {
        this.rxCommandFactoryService = rxCommandFactoryService;
        this.startProcessCommand = this.rxCommandFactoryService.forResourceType('com.bmc.arsys.rx.application.process.command.StartProcessInstanceCommand');
    }
    start(processDefinitionName, processInputValues) {
        return this.startProcessCommand.execute({
            processDefinitionName,
            processInputValues
        }, {
            observe: 'response',
            responseType: 'text'
        });
    }
}
RxProcessInstanceCommandsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceCommandsService, deps: [{ token: i1.RxCommandFactoryService }], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessInstanceCommandsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceCommandsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceCommandsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.RxCommandFactoryService }]; } });

class RxProcessInstanceCountsByStatusDataPageService extends DataPage {
    constructor(injector) {
        super(injector, 'com.bmc.arsys.rx.application.process.datapage.ProcessInstanceCountsByStatusDataPageQuery');
        this.injector = injector;
    }
}
RxProcessInstanceCountsByStatusDataPageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceCountsByStatusDataPageService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessInstanceCountsByStatusDataPageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceCountsByStatusDataPageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceCountsByStatusDataPageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

class RxProcessInstanceDataPageService extends DataPage {
    constructor(injector) {
        super(injector, 'com.bmc.arsys.rx.application.process.datapage.ProcessInstanceDataPageQuery');
        this.injector = injector;
    }
}
RxProcessInstanceDataPageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceDataPageService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessInstanceDataPageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceDataPageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceDataPageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

class RxProcessDesignerDependencyResolver {
    constructor(rxBundleService, rxGlobalCacheService, rxLogService, rxProcessElementRegistryService) {
        this.rxBundleService = rxBundleService;
        this.rxGlobalCacheService = rxGlobalCacheService;
        this.rxLogService = rxLogService;
        this.rxProcessElementRegistryService = rxProcessElementRegistryService;
    }
    resolve() {
        let dependencyBundleDescriptors = [];
        return this.rxGlobalCacheService.getLicensedBundleDescriptors().pipe(map((bundleDescriptors) => bundleDescriptors.filter((bundleDescriptor) => !isEmpty(bundleDescriptor.uiOptions?.serverActions) || !isEmpty(bundleDescriptor.uiOptions?.callActivities))), tap((bundleDescriptors) => (dependencyBundleDescriptors = bundleDescriptors)), map((bundleDescriptors) => bundleDescriptors.map((bundleDescriptor) => bundleDescriptor.id)), switchMap((bundleIds) => bundleIds.length ? this.rxBundleService.loadBundles(bundleIds, true) : of(bundleIds)), switchMap((bundleContexts) => forkJoin([
            this.rxProcessElementRegistryService.unresolvedCallActivityDescriptors$,
            this.rxProcessElementRegistryService.unresolvedServerActionDescriptors$
        ]).pipe(map(() => bundleContexts))), tap((bundleContexts) => {
            const registeredServerActionNames = new Set(this.rxProcessElementRegistryService.getServerActionNames());
            forEach(bundleContexts, (bundleContext) => {
                if (bundleContext.isBundleCompiled) {
                    const bundleDescriptor = find(dependencyBundleDescriptors, {
                        id: bundleContext.bundleId
                    });
                    if (bundleDescriptor) {
                        const unregisteredServerActionNames = bundleDescriptor.uiOptions.serverActions.filter((serverActionName) => !registeredServerActionNames.has(serverActionName));
                        if (!isEmpty(unregisteredServerActionNames)) {
                            this.rxLogService.warning(`[Process Designer Dependency] ${bundleDescriptor.id}: Unregistered server actions found in the manifest\n${unregisteredServerActionNames}`);
                        }
                    }
                }
            });
            const registeredCalledProcessDefinitionNames = new Set(this.rxProcessElementRegistryService.getCalledProcessDefinitionNames());
            forEach(bundleContexts, (bundleContext) => {
                if (bundleContext.isBundleCompiled) {
                    const bundleDescriptor = find(dependencyBundleDescriptors, {
                        id: bundleContext.bundleId
                    });
                    if (bundleDescriptor) {
                        const unregisteredCalledProcessDefinitionNames = bundleDescriptor.uiOptions.callActivities.filter((calledProcessDefinitionName) => !registeredCalledProcessDefinitionNames.has(calledProcessDefinitionName));
                        if (!isEmpty(unregisteredCalledProcessDefinitionNames)) {
                            this.rxLogService.warning(`[Process Designer Dependency] ${bundleDescriptor.id}: Unregistered call activities found in the manifest \n ${unregisteredCalledProcessDefinitionNames}`);
                        }
                    }
                }
            });
        }), shareReplay(1));
    }
}
RxProcessDesignerDependencyResolver.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDesignerDependencyResolver, deps: [{ token: i1.RxBundleService }, { token: i1.RxGlobalCacheService }, { token: i1.RxLogService }, { token: RxProcessElementRegistryService }], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessDesignerDependencyResolver.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDesignerDependencyResolver, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessDesignerDependencyResolver, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.RxBundleService }, { type: i1.RxGlobalCacheService }, { type: i1.RxLogService }, { type: RxProcessElementRegistryService }]; } });

const RxServerAction = ɵmakeDecorator('RxServerAction');

/**
 * Generated bundle index. Do not edit.
 */

export { ElementVisibilityOptions, ElementVisibilityOptionsOld, ListFieldDefinitionSubType, RX_PROCESS_DEFINITION, RX_PROCESS_INSTANCE, RxProcessDataDictionaryService, RxProcessDefinitionCacheService, RxProcessDefinitionDataPageService, RxProcessDefinitionService, RxProcessDesignerDependencyResolver, RxProcessElementRegistryService, RxProcessElementSearchService, RxProcessElementsService, RxProcessInstanceCommandsService, RxProcessInstanceCountsByStatusDataPageService, RxProcessInstanceDataPageService, RxProcessInstanceService, RxServerAction };
